import React, { useState, useMemo } from "react";
import { Button } from "../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../design/dialog";
import { Input } from "../design/input";
import {
  ErrorMessage,
  Field,
  Label,
} from "../design/fieldset";
import { Select } from "../design/select";
import { useVisit } from "../contexts/VisitContext";
import { useSupervisor } from "../contexts/SupervisorContext";
import { useCase } from "../contexts/CaseContext";
import { useLocation } from "../contexts/LocationContext";
import { Timestamp } from "firebase/firestore";
import { Textarea } from "../design/textarea";
import { Location } from "@visitproof/types";
import { Checkbox, CheckboxField } from "../design/checkbox";
import { Switch, SwitchField } from "../design/switch";
import moment from "moment-timezone";
import { Visit } from "@visitproof/types";

interface Props {
  caseId?: string | null;
  supervisorId?: string | null;
}

interface Errors {
  startDate?: string;
  startTime?: string;
  endDate?: string;
  endTime?: string;
  caseId?: string;
  supervisorId?: string;
  timeZoneId?: string;
  days?: string;
}

interface VisitData {
  startDate: Date;
  endDate: Date;
}

// Common US time zones for easier selection
const COMMON_TIMEZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Phoenix",
  "America/Anchorage",
  "Pacific/Honolulu",
];

const DAY_ORDER = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"] as const;

const CreateMultipleVisitsButton: React.FC<Props> = ({
  caseId: initialCaseId,
  supervisorId: initialSupervisorId,
}) => {
  const { cases } = useCase();
  const { locations } = useLocation();
  const { supervisors } = useSupervisor();
  const { createVisit } = useVisit();

  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [startDate, setStartDate] = useState<string>(() => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  });
  const [endDate, setEndDate] = useState<string>(() => {
    const threeMonthsLater = new Date();
    threeMonthsLater.setMonth(threeMonthsLater.getMonth() + 3);
    return threeMonthsLater.toISOString().split("T")[0];
  });
  const [startTime, setStartTime] = useState<string>("10:00");
  const [endTime, setEndTime] = useState<string>("12:00");
  const [location, setLocation] = useState<Location | null>(null);
  const [caseId, setCaseId] = useState<string | null>(initialCaseId ?? null);
  const [supervisorId, setSupervisorId] = useState<string | null>(
    initialSupervisorId ?? null,
  );
  const [notes, setNotes] = useState<string>("");
  const [days, setDays] = useState<Record<string, boolean>>({
    sunday: false,
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
  });
  const [frequency, setFrequency] = useState<string>("weekly");
  const [markAsScheduled, setMarkAsScheduled] = useState(false);
  const [dates, setDates] = useState<VisitData[]>([]);
  const [errors, setErrors] = useState<Errors>({});
  const [isSaving, setIsSaving] = useState(false);
  const [timeZoneId, setTimeZoneId] = useState(() => moment.tz.guess());

  // Check if any day is selected
  const hasSelectedDays = useMemo(() => {
    return Object.values(days).some(Boolean);
  }, [days]);

  const validateForm = () => {
    const newErrors: Errors = {};
    if (!startDate.trim()) newErrors.startDate = "Start date is required.";
    if (!endDate.trim()) newErrors.endDate = "End date is required.";
    if (!startTime.trim()) newErrors.startTime = "Start time is required.";
    if (!endTime.trim()) newErrors.endTime = "End time is required.";
    if (!caseId) newErrors.caseId = "Case is required.";
    if (!timeZoneId) newErrors.timeZoneId = "Time zone is required.";
    if (!hasSelectedDays) newErrors.days = "Please select at least one day of the week.";

    const start = new Date(startDate);
    const end = new Date(endDate);
    if (start > end) {
      newErrors.endDate = "End date must be after start date.";
    }

    // Validate time - end time should be after start time
    const [startHour, startMin] = startTime.split(":").map(Number);
    const [endHour, endMin] = endTime.split(":").map(Number);
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    if (endMinutes <= startMinutes) {
      newErrors.endTime = "End time must be after start time.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const generateDates = (): VisitData[] => {
    if (!timeZoneId) return [];

    const visitsToCreate: VisitData[] = [];
    const startMoment = moment(startDate);
    const endMoment = moment(endDate);

    // Track weeks for bi-weekly frequency
    let weekCount = 0;
    let lastWeekNumber = startMoment.week();

    let current = startMoment.clone();

    while (current.isSameOrBefore(endMoment)) {
      const dayOfWeek = current.day();
      const dayName = DAY_ORDER[dayOfWeek];
      const currentWeekNumber = current.week();

      // Track when we move to a new week
      if (currentWeekNumber !== lastWeekNumber) {
        weekCount++;
        lastWeekNumber = currentWeekNumber;
      }

      // Check if we should include this day based on frequency
      let shouldInclude = days[dayName];

      if (shouldInclude && frequency === "bi-weekly") {
        // Only include every other week (even weeks)
        shouldInclude = weekCount % 2 === 0;
      }

      if (shouldInclude) {
        const visitStart = moment.tz(
          `${current.format("YYYY-MM-DD")} ${startTime}`,
          "YYYY-MM-DD HH:mm",
          timeZoneId,
        );
        const visitEnd = moment.tz(
          `${current.format("YYYY-MM-DD")} ${endTime}`,
          "YYYY-MM-DD HH:mm",
          timeZoneId,
        );
        visitsToCreate.push({
          startDate: visitStart.toDate(),
          endDate: visitEnd.toDate(),
        });
      }

      current.add(1, "day");
    }

    return visitsToCreate;
  };

  const createVisits = async () => {
    setIsSaving(true);
    try {
      const caseItem = cases.find((c) => c.id === caseId);
      for (const date of dates) {
        let data = {
          status: markAsScheduled ? "scheduled" : "pending",
          caseId: caseId!,
          startDate: Timestamp.fromDate(date.startDate),
          endDate: Timestamp.fromDate(date.endDate),
          parents: caseItem?.parents,
          parentIds: caseItem?.parentIds,
          children: caseItem?.children,
          childIds: caseItem?.childIds,
          notes,
          timeZoneId,
        } as Visit;
        if (supervisorId) {
          data = {
            ...data,
            supervisorId: supervisorId!,
            supervisor: supervisors.find((s) => s.id === supervisorId),
          };
        }
        if (location && location.id) {
          data = {
            ...data,
            locationId: location.id,
            location,
          };
        }

        await createVisit(data);
      }
      clearForm();
    } catch (error) {
      console.error("Error creating visits:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (step === 0 && validateForm()) {
      const generatedDates = generateDates();
      setDates(generatedDates);
      setStep(1);
    } else if (step === 1) {
      await createVisits();
    }
  };

  const clearForm = () => {
    setStep(0);
    const today = new Date();
    setStartDate(today.toISOString().split("T")[0]);
    const threeMonthsLater = new Date();
    threeMonthsLater.setMonth(threeMonthsLater.getMonth() + 3);
    setEndDate(threeMonthsLater.toISOString().split("T")[0]);
    setStartTime("10:00");
    setEndTime("12:00");
    setCaseId(initialCaseId ?? null);
    setSupervisorId(initialSupervisorId ?? null);
    setLocation(null);
    setNotes("");
    setDays({
      sunday: false,
      monday: false,
      tuesday: false,
      wednesday: false,
      thursday: false,
      friday: false,
      saturday: false,
    });
    setFrequency("weekly");
    setMarkAsScheduled(false);
    setTimeZoneId(moment.tz.guess());
    setErrors({});
    setDates([]);
    setIsOpen(false);
  };

  // Get filtered locations for selected case
  const filteredLocations = useMemo(() => {
    return locations.filter((loc) => loc.caseId === caseId);
  }, [locations, caseId]);

  // Format date for display
  const formatVisitDate = (date: Date) => {
    return moment(date).tz(timeZoneId).format("ddd, MMM D, YYYY");
  };

  const formatVisitTime = (start: Date, end: Date) => {
    const startFormatted = moment(start).tz(timeZoneId).format("h:mm A");
    const endFormatted = moment(end).tz(timeZoneId).format("h:mm A");
    return `${startFormatted} - ${endFormatted}`;
  };

  return (
    <>
      <Button onClick={() => setIsOpen(true)}>Create Multiple Visits</Button>
      <Dialog open={isOpen} onClose={clearForm} size="2xl">
        <DialogTitle>
          {step === 0 ? "Schedule Recurring Visits" : "Review & Confirm Visits"}
        </DialogTitle>

        <DialogBody className="space-y-8 max-h-[70vh] overflow-y-auto">
          {step === 0 && (
            <>
              {/* Case Selection */}
              <section className="space-y-6">
                <div>
                  <h4 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
                    Case Selection
                  </h4>
                  <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                    Select the case for these visits
                  </p>
                </div>
                <Field>
                  <Label>Case</Label>
                  <Select
                    value={caseId || ""}
                    onChange={(e) => {
                      setCaseId(e.target.value);
                      setLocation(null); // Reset location when case changes
                    }}
                    disabled={isSaving}
                  >
                    <option value="">Select a case</option>
                    {cases
                      .filter((caseItem) => caseItem.status !== "closed")
                      .map((caseItem) => (
                        <option key={caseItem.id} value={caseItem.id}>
                          {`${caseItem.identifier ?? "Unnamed Case"} - (${Object.values(
                            caseItem.parents ?? {},
                          )
                            .map((p) => `${p.firstName} ${p.lastName}`)
                            .join(", ")})`}
                        </option>
                      ))}
                  </Select>
                  {errors.caseId && <ErrorMessage>{errors.caseId}</ErrorMessage>}
                </Field>
              </section>

              <hr className="border-zinc-200 dark:border-zinc-700" />

              {/* Date Range */}
              <section className="space-y-6">
                <div>
                  <h4 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
                    Date Range
                  </h4>
                  <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                    Define the period for recurring visits
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <Field>
                    <Label>Start Date</Label>
                    <Input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      disabled={isSaving}
                    />
                    {errors.startDate && (
                      <ErrorMessage>{errors.startDate}</ErrorMessage>
                    )}
                  </Field>
                  <Field>
                    <Label>End Date</Label>
                    <Input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      disabled={isSaving}
                    />
                    {errors.endDate && (
                      <ErrorMessage>{errors.endDate}</ErrorMessage>
                    )}
                  </Field>
                </div>
              </section>

              <hr className="border-zinc-200 dark:border-zinc-700" />

              {/* Visit Time */}
              <section className="space-y-6">
                <div>
                  <h4 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
                    Visit Time
                  </h4>
                  <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                    Each visit will be scheduled at these times
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
                  <Field>
                    <Label>Start Time</Label>
                    <Input
                      type="time"
                      step="900"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      disabled={isSaving}
                    />
                    {errors.startTime && (
                      <ErrorMessage>{errors.startTime}</ErrorMessage>
                    )}
                  </Field>
                  <Field>
                    <Label>End Time</Label>
                    <Input
                      type="time"
                      step="900"
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                      disabled={isSaving}
                    />
                    {errors.endTime && (
                      <ErrorMessage>{errors.endTime}</ErrorMessage>
                    )}
                  </Field>
                  <Field>
                    <Label>Time Zone</Label>
                    <Select
                      value={timeZoneId}
                      onChange={(e) => setTimeZoneId(e.target.value)}
                      disabled={isSaving}
                    >
                      <optgroup label="Common Time Zones">
                        {COMMON_TIMEZONES.map((tz) => (
                          <option key={tz} value={tz}>
                            {tz.replace(/_/g, " ")} ({moment.tz(tz).format("z")})
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="All Time Zones">
                        {moment.tz.names()
                          .filter((tz) => !COMMON_TIMEZONES.includes(tz))
                          .map((tz) => (
                            <option key={tz} value={tz}>
                              {tz}
                            </option>
                          ))}
                      </optgroup>
                    </Select>
                    {errors.timeZoneId && (
                      <ErrorMessage>{errors.timeZoneId}</ErrorMessage>
                    )}
                  </Field>
                </div>
              </section>

              <hr className="border-zinc-200 dark:border-zinc-700" />

              {/* Frequency & Days */}
              <section className="space-y-6">
                <div>
                  <h4 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
                    Recurrence Pattern
                  </h4>
                  <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                    Select how often and on which days visits should occur
                  </p>
                </div>
                <Field>
                  <Label>Frequency</Label>
                  <Select
                    value={frequency}
                    onChange={(e) => setFrequency(e.target.value)}
                    disabled={isSaving}
                    className="sm:max-w-xs"
                  >
                    <option value="weekly">Every week</option>
                    <option value="bi-weekly">Every other week</option>
                  </Select>
                </Field>
                <div>
                  <span className="block mb-3 text-base/6 font-medium text-zinc-950 sm:text-sm/6 dark:text-white">
                    Days of the Week
                  </span>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
                    {DAY_ORDER.map((day) => (
                      <CheckboxField key={day} className="flex items-center gap-2">
                        <Checkbox
                          checked={days[day]}
                          onChange={() =>
                            setDays({
                              ...days,
                              [day]: !days[day],
                            })
                          }
                        />
                        <Label className="text-sm font-normal">
                          {day.charAt(0).toUpperCase() + day.slice(1, 3)}
                        </Label>
                      </CheckboxField>
                    ))}
                  </div>
                  {errors.days && (
                    <p className="mt-2 text-base/6 text-red-600 sm:text-sm/6 dark:text-red-500">
                      {errors.days}
                    </p>
                  )}
                </div>
              </section>

              <hr className="border-zinc-200 dark:border-zinc-700" />

              {/* Optional: Location & Monitor */}
              <section className="space-y-6">
                <div>
                  <h4 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
                    Assignment (Optional)
                  </h4>
                  <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                    Optionally assign a location and monitor to all visits
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <Field>
                    <Label>Location</Label>
                    <Select
                      value={location?.id || ""}
                      onChange={(e) =>
                        setLocation(
                          locations.find((loc) => loc.id === e.target.value) || null,
                        )
                      }
                      disabled={!caseId || filteredLocations.length === 0}
                    >
                      <option value="">Select a location</option>
                      {filteredLocations.map((loc) => (
                        <option key={loc.id} value={loc.id}>
                          {loc.name}
                        </option>
                      ))}
                    </Select>
                    {!caseId && (
                      <p className="mt-1 text-xs text-zinc-500">Select a case first</p>
                    )}
                  </Field>
                  <Field>
                    <Label>Monitor</Label>
                    <Select
                      value={supervisorId || ""}
                      onChange={(e) => setSupervisorId(e.target.value || null)}
                      disabled={isSaving}
                    >
                      <option value="">Select a monitor</option>
                      {supervisors
                        .filter((s) => s.status !== "inactive")
                        .map((supervisor) => (
                          <option key={supervisor.id} value={supervisor.id}>
                            {`${supervisor.firstName ?? ""} ${supervisor.lastName ?? ""}`.trim() || "Unnamed Monitor"}
                          </option>
                        ))}
                    </Select>
                  </Field>
                </div>
              </section>

              <hr className="border-zinc-200 dark:border-zinc-700" />

              {/* Notes */}
              <section className="space-y-6">
                <div>
                  <h4 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
                    Notes
                  </h4>
                  <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                    These notes will be added to all created visits
                  </p>
                </div>
                <Field>
                  <Textarea
                    rows={3}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    disabled={isSaving}
                    placeholder="Enter any notes for these visits..."
                  />
                </Field>
              </section>

              <hr className="border-zinc-200 dark:border-zinc-700" />

              {/* Status */}
              <section className="space-y-6">
                <div>
                  <h4 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
                    Visit Status
                  </h4>
                  <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                    Choose the initial status for all created visits
                  </p>
                </div>
                <SwitchField>
                  <Label>Mark as Scheduled</Label>
                  <p className="text-sm text-zinc-500 dark:text-zinc-400">
                    Enable this to set all visits to "Scheduled" instead of "Pending"
                  </p>
                  <Switch
                    checked={markAsScheduled}
                    onChange={setMarkAsScheduled}
                    disabled={isSaving}
                  />
                </SwitchField>
              </section>
            </>
          )}

          {step === 1 && (
            <>
              {/* Summary */}
              <section className="space-y-4">
                <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                  <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                    {dates.length} visit{dates.length !== 1 ? "s" : ""} will be created
                  </p>
                  <p className="mt-1 text-sm text-blue-600 dark:text-blue-300">
                    {frequency === "weekly" ? "Weekly" : "Bi-weekly"} on{" "}
                    {DAY_ORDER.filter((d) => days[d])
                      .map((d) => d.charAt(0).toUpperCase() + d.slice(1))
                      .join(", ")}
                  </p>
                </div>
              </section>

              {/* Visit Preview */}
              <section className="space-y-4">
                <div>
                  <h4 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
                    Visit Schedule Preview
                  </h4>
                  <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                    Review the visits that will be created
                  </p>
                </div>
                <div className="max-h-64 overflow-y-auto border rounded-lg border-zinc-200 dark:border-zinc-700">
                  <table className="min-w-full divide-y divide-zinc-200 dark:divide-zinc-700">
                    <thead className="bg-zinc-50 dark:bg-zinc-800 sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">
                          #
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">
                          Time
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-zinc-900 divide-y divide-zinc-200 dark:divide-zinc-700">
                      {dates.map((visit, index) => (
                        <tr key={index}>
                          <td className="px-4 py-2 text-sm text-zinc-500 dark:text-zinc-400">
                            {index + 1}
                          </td>
                          <td className="px-4 py-2 text-sm text-zinc-900 dark:text-zinc-100">
                            {formatVisitDate(visit.startDate)}
                          </td>
                          <td className="px-4 py-2 text-sm text-zinc-500 dark:text-zinc-400">
                            {formatVisitTime(visit.startDate, visit.endDate)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              {/* Assignment Summary */}
              {(location || supervisorId) && (
                <section className="space-y-4">
                  <div>
                    <h4 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
                      Assignment
                    </h4>
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {location && (
                      <div className="p-3 rounded-lg bg-zinc-50 dark:bg-zinc-800">
                        <p className="text-xs text-zinc-500 dark:text-zinc-400">Location</p>
                        <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                          {location.name}
                        </p>
                      </div>
                    )}
                    {supervisorId && (
                      <div className="p-3 rounded-lg bg-zinc-50 dark:bg-zinc-800">
                        <p className="text-xs text-zinc-500 dark:text-zinc-400">Monitor</p>
                        <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                          {(() => {
                            const supervisor = supervisors.find((s) => s.id === supervisorId);
                            return supervisor
                              ? `${supervisor.firstName ?? ""} ${supervisor.lastName ?? ""}`.trim()
                              : "Unknown";
                          })()}
                        </p>
                      </div>
                    )}
                  </div>
                </section>
              )}
            </>
          )}
        </DialogBody>

        <DialogActions>
          <Button plain onClick={step === 0 ? clearForm : () => setStep(0)}>
            {step === 0 ? "Cancel" : "Back"}
          </Button>
          <Button disabled={isSaving} onClick={handleSubmit}>
            {step === 0 ? "Preview Visits" : isSaving ? "Creating..." : `Create ${dates.length} Visit${dates.length !== 1 ? "s" : ""}`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreateMultipleVisitsButton;
